import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronUp } from 'lucide-react';

const NAV_LINKS = [
  { to: '/', label: 'Home' },
  { to: '/platform', label: 'Platform' },
  { to: '/for-pharma', label: 'For Pharma' },
  { to: '/for-biotech', label: 'For Biotech' },
  { to: '/case-studies', label: 'Case Studies' },
  { to: '/about', label: 'About' },
  { to: '/apply', label: 'Get Started', cta: true },
];

export default function PublicNav() {
  const [scrolled, setScrolled] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  // Copy protection — disable right-click and copy shortcuts on public pages
  useEffect(() => {
    const preventContext = (e) => e.preventDefault();
    const preventCopy = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === 'c' || e.key === 'C' || e.key === 'a' || e.key === 'A')) {
        e.preventDefault();
      }
    };
    document.addEventListener('contextmenu', preventContext);
    document.addEventListener('keydown', preventCopy);
    return () => {
      document.removeEventListener('contextmenu', preventContext);
      document.removeEventListener('keydown', preventCopy);
    };
  }, []);

  useEffect(() => {
    const handler = () => {
      setScrolled(window.scrollY > 40);
      setShowBackToTop(window.scrollY > 400);
    };
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  useEffect(() => setMobileOpen(false), [location]);

  const navBg = scrolled
    ? 'backdrop-blur-2xl bg-[#0A1128]/80 border-b border-white/10 shadow-sm'
    : 'bg-transparent';

  return (
    <>
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${navBg}`} data-testid="public-nav">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-16 lg:h-20">
          <Link to="/" className="flex items-center" data-testid="nav-logo">
            <img
              src="https://customer-assets.emergentagent.com/job_licensing-hub-3/artifacts/0s2i33uj_MM_Forsight%20Logo%20Png.png"
              alt="ForesightRx"
              className="h-9 lg:h-11 w-auto brightness-0 invert"
            />
          </Link>

          {/* Desktop */}
          <div className="hidden lg:flex items-center gap-1">
            {NAV_LINKS.map(link => (
              link.cta ? (
                <Link
                  key={link.to}
                  to={link.to}
                  className="ml-4 px-7 py-2.5 rounded-full bg-[#70C24C] text-[#0A1128] text-sm font-bold hover:bg-[#5fad3b] hover:-translate-y-0.5 transition-all duration-300 shadow-[0_0_20px_rgba(112,194,76,0.2)]"
                  data-testid="nav-get-started-cta"
                >
                  {link.label}
                </Link>
              ) : (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`px-4 py-2 text-sm font-medium transition-all duration-300 rounded-lg ${
                    location.pathname === link.to
                      ? 'text-white bg-white/10'
                      : 'text-[#A1A8C3] hover:text-white hover:bg-white/5'
                  }`}
                  data-testid={`nav-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                >
                  {link.label}
                </Link>
              )
            ))}
          </div>

          {/* Mobile toggle */}
          <button
            className="lg:hidden p-2 rounded-lg text-white"
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="nav-mobile-toggle"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden backdrop-blur-2xl bg-[#0A1128]/95 border-t border-white/10" data-testid="nav-mobile-menu">
          <div className="px-6 py-4 space-y-1">
            {NAV_LINKS.map(link => (
              <Link
                key={link.to}
                to={link.to}
                className={`block px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                  link.cta
                    ? 'bg-[#70C24C] text-[#0A1128] text-center mt-3 font-bold'
                    : location.pathname === link.to
                      ? 'bg-white/10 text-white'
                      : 'text-[#A1A8C3] hover:bg-white/5 hover:text-white'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </nav>

    {/* Back to Top Button */}
    {showBackToTop && (
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="fixed bottom-8 right-8 z-50 w-11 h-11 rounded-full bg-[#70C24C] text-[#0A1128] shadow-lg hover:bg-[#5fad3b] hover:-translate-y-1 transition-all duration-300 flex items-center justify-center shadow-[0_0_20px_rgba(112,194,76,0.3)]"
        data-testid="back-to-top-btn"
        aria-label="Back to top"
      >
        <ChevronUp className="w-5 h-5" />
      </button>
    )}
    </>
  );
}
